import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";
import { Spotlight } from "@/components/AppTour";
import { HelpCircle } from "lucide-react";

const t = (sel, title, tip) => ({ sel, title, tip });
export const PAGE_TOURS = {
  "/cleaning": [
    t('[data-testid^="cleaning-col-"]', "Turnover columns", "Each card moves left to right: To do → In progress → Done."),
    t('[data-testid^="cleaning-card-"]', "A turnover card", "Property, date, cleaner and access. Drag or use the arrows to move it along."),
    t('[data-testid^="cleaning-photos-"]', "Room photos", "Photograph every room on the checklist — Done is blocked until all rooms are covered."),
    t('[data-testid^="cleaning-report-damage-"]', "Report damage", "Spotted guest damage? Log it with photos so we can charge the deposit."),
    t('[data-testid="cleaner-widget"]', "Cleaner messages", "Preview the SMS cleaners receive and refresh today's rota."),
  ],
  "/damage-reports": [
    t('[data-testid="damage-tabs"]', "Reports & Trends", "Switch to Trends to see the most damaged properties and items."),
    t('[data-testid="damage-filter"]', "Filters", "Drafts are AI-spotted changes waiting for you to confirm or discard."),
    t('[data-testid^="damage-report-"]', "A damage report", "Notes, photos, guest and stay — click photos to enlarge."),
    t('[data-testid^="damage-suggest-cost-"]', "Suggest cost", "AI estimates a UK repair cost range, calibrated from your past charges."),
    t('[data-testid^="damage-charge-btn-"]', "Charge deposit", "Takes it from the Stripe deposit hold, or logs a deduction with a Pay now invoice."),
  ],
  "/bookings": [
    t('[data-testid="bookings-add-btn"]', "New booking", "Add a direct or phone booking — it blocks the calendar instantly."),
    t('[data-testid="bookings-table"]', "All reservations", "Every channel in one list. A red damage badge means the guest has caused damage before."),
    t('[data-testid^="bookings-journey-"]', "Check-in link", "Sends the guest journey: ID, agreement, deposit and balance."),
    t('[data-testid^="bookings-refund-"]', "Refunds", "Issue a full or partial Stripe refund with a reason."),
  ],
  "/properties": [
    t('[data-testid="properties-add-btn"]', "New property", "Add a unit — address, bedrooms, access points and channel links."),
    t('[data-testid="properties-ical-sync-now"]', "Sync calendars", "Pulls the latest Airbnb / Booking.com iCal bookings now."),
    t('[data-testid="properties-root"]', "Your properties", "Click a card to edit access codes, the photo checklist, lease and compliance."),
    t('[data-testid^="properties-reveal-access-"]', "Access codes", "Codes stay hidden until revealed — every reveal is logged."),
    t('[data-testid="properties-audit-btn"]', "Access audit", "See who viewed which access codes and when."),
  ],
  "/finance": [
    t('[data-testid="finance-tabs"]', "Expenses & income", "Switch between money out and money in."),
    t('[data-testid="finance-filter-property"]', "Property filter", "Narrow everything to one property for its profit picture."),
    t('[data-testid="finance-add-expense-btn"]', "Add expense", "Log bills, repairs and supplies — or import a CSV."),
    t('[data-testid="finance-grand-total"]', "Grand total", "Running total for the current filter."),
    t('[data-testid="finance-expenses-table"]', "The ledger", "Every entry with date, category and property."),
  ],
  "/inbox": [
    t('[data-testid="inbox-search"]', "Search", "Find any message by guest, phone or text."),
    t('[data-testid^="inbox-filter-"]', "Channel filters", "Switch between email, SMS and WhatsApp threads."),
    t('[data-testid^="inbox-item-"]', "A conversation", "Open it to reply or use an AI draft. Red damage badges flag returning guests with past damage."),
    t('[data-testid="inbox-templates-strip"]', "Quick templates", "Insert a saved reply in one click."),
    t('[data-testid="inbox-snooze-menu"]', "Snooze & quiet hours", "Pause the chime during meetings or overnight."),
  ],
  "/calendar": [
    t('[data-testid="calendar-root"]', "The grid", "One row per property, one column per day — bookings and turnovers at a glance."),
    t('[data-testid="calendar-prev-btn"]', "Move between weeks", "Use the arrows to go back or forward."),
    t('[data-testid="calendar-today-btn"]', "Jump to today", "Brings the grid back to the current week."),
    t('[data-testid^="cal-cell-"]', "Edit a night", "Click a cell to change the nightly rate, minimum stay or block it."),
  ],
  "/statements": [
    t('[data-testid="statements-month-select"]', "Pick the month", "Choose the month (and year) you're reporting on."),
    t('[data-testid="statements-table"]', "Owner statements", "Income, costs and net payout per landlord — download any statement as a PDF."),
    t('[data-testid="statement-queue-card"]', "Approval queue", "Review generated statements before they go to landlords."),
    t('[data-testid="statement-queue-send"]', "Approve & send", "Emails the approved statements to each landlord."),
  ],
  "/settings": [
    t('[data-testid="settings-2fa-toggle"]', "Profile & 2FA", "Change your password and turn on two-factor sign-in."),
    t('[data-testid="settings-ota-bridge"]', "Integrations", "Channel links and connected services (Stripe, Twilio, email, calendars)."),
    t('[data-testid="settings-weekly-digest-card"]', "Automations", "Digest emails, timings and what gets sent automatically."),
    t('[data-testid="settings-checkout-photos"]', "Photo check-out", "Require cleaners to photograph every room before Done."),
    t('[data-testid="settings-deposit-boost"]', "Damage settings", "Deposit boost for repeat-damage guests, plus house-care tips below."),
  ],
  "/contacts": [
    t('[data-testid="contacts-add-btn"]', "New contact", "Add a guest, landlord, contractor or supplier."),
    t('[data-testid="contacts-search-input"]', "Search", "Find anyone by name, email or phone."),
    t('[data-testid^="contacts-tab-"]', "Type tabs", "Filter by guests, contractors, suppliers or owners."),
    t('[data-testid="contacts-table"]', "Your contacts", "Click a row for full history — stays, messages, documents and damage."),
  ],
  "/journeys": [
    t('[data-testid="journeys-search"]', "Search journeys", "Find a guest's check-in journey by name or property."),
    t('[data-testid^="journey-row-"]', "A journey", "Shows ID, agreement, deposit and balance progress for the stay."),
    t('[data-testid^="journey-timeline-"]', "Timeline", "Every step the guest has completed, with timestamps."),
    t('[data-testid^="journey-capture-"]', "Capture deposit", "Take part or all of the deposit hold, with photos and a reason."),
    t('[data-testid^="journey-email-resend-"]', "Resend link", "Nudge a guest who hasn't finished their check-in steps."),
  ],
  "/campaigns": [
    t('[data-testid="campaigns-new-btn"]', "New campaign", "Start an email blast to guests or landlords."),
    t('[data-testid="campaigns-segment-type"]', "Audience", "Choose who receives it — by type or tags like VIP."),
    t('[data-testid="campaigns-preview-btn"]', "Preview & test", "Check how it looks and send yourself a test first."),
    t('[data-testid="quiet-offers-card"]', "Quiet month offers", "Fill gaps with a discount offer to past guests."),
  ],
};

const pageKey = (path) => (PAGE_TOURS[path] ? path : null);

const BUILDER_STEPS = [
  t('[data-testid="campaigns-template-select"]', "Start from a template", "Pick a saved template to pre-fill the subject and body — or write your own."),
  t('[data-testid="campaigns-segment-type"]', "Audience", "Choose who receives it: guests, landlords, contractors, or tags like VIP."),
  t('[data-testid="campaigns-subject-input"]', "Subject & message", "Merge tags like {{first_name}} personalise every email."),
  t('[data-testid="campaigns-preview-btn"]', "Preview", "See exactly what recipients will get before sending."),
  t('[data-testid="campaigns-send-test-btn"]', "Send a test", "Email yourself a copy to check it on your phone."),
  t('[data-testid="campaigns-send-btn"]', "Send", "Sends now — or set a date above to schedule it."),
];

export function PageTourButton() {
  const { pathname } = useLocation();
  if (!pageKey(pathname)) return null;
  return (
    <button onClick={() => window.dispatchEvent(new CustomEvent("ns-page-tour", { detail: pathname }))} data-testid="page-tour-btn"
      className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-teal-200 text-teal-700 bg-white/70 hover:bg-teal-50 transition-colors">
      <HelpCircle className="w-3.5 h-3.5" /> How this page works
    </button>
  );
}

export const DIALOG_TOURS = {
  "/campaigns#builder": { prefix: "builder-tour", steps: BUILDER_STEPS },
  "/bookings#new": { prefix: "new-booking-tour", steps: [
    t('[data-testid="bookings-field-contact"]', "Guest", "Pick the guest — add them in Contacts first if they're new."),
    t('[data-testid="bookings-field-property"]', "Property", "Choose the unit; the calendar blocks these nights straight away."),
    t('[data-testid="bookings-field-dates"]', "Dates & guests", "Check-in and check-out dates, plus the number of guests."),
    t('[data-testid="bookings-total-price"]', "Price & referral", "Total price; a valid guest referral code takes 10% off automatically."),
    t('[data-testid="bookings-save-btn"]', "Save booking", "Saves it — then send the check-in link from the bookings list."),
  ] },
  "/properties#edit": { prefix: "property-tour", steps: [
    t('[data-testid="properties-name-input"]', "Name & address", "The unit name and full address — the postcode is used for cleaner GPS check-in."),
    t('[data-testid="properties-access-section"]', "Access points", "Key safes, door codes and smart locks. Codes stay hidden for cleaners until they tap I'm here."),
    t('[data-testid="properties-lease-section"]', "Lease", "Start/end dates, rent and landlord — renewal alerts go out at 90, 30 and 7 days."),
    t('[data-testid="photo-rooms-editor"]', "Photo checklist", "The rooms cleaners must photograph before Done. Pre-filled from bedrooms and bathrooms — add or remove rooms here."),
    t('[data-testid="properties-save-btn"]', "Save", "Saves the property. Compliance documents below save as you upload."),
  ] },
  "/damage#charge": { prefix: "charge-tour", steps: [
    t('[data-testid="charge-deposit-method"]', "Stripe hold or manual?", "Green = the guest has an open Stripe deposit hold, so the money is captured from their card straight away. Amber = no hold (released, expired or never taken), so it's logged as a manual deduction instead."),
    t('[data-testid="charge-deposit-amount"]', "Amount", "Pre-filled from the estimate. With a Stripe hold you can't charge more than what's still held."),
    t('[data-testid="charge-deposit-reason"]', "Reason", "Shown to the guest on their invoice, so keep it clear and polite."),
    t('[data-testid="charge-deposit-submit"]', "Charge", "Both routes email a PDF invoice with photos and log the income in Finance. Manual deductions add a Stripe Pay now link, with reminders at 7 and 14 days."),
  ] },
  "/bookings#journey": { prefix: "journey-tour", steps: [
    t('[data-testid="journey-deposit-amount"]', "Deposit hold", "The card hold for damage. Repeat-damage guests are raised automatically — a red note explains why."),
    t('[data-testid="journey-steps"]', "Check-in steps", "Choose what the guest must do: photo ID, e-sign house rules, deposit and balance."),
    t('[data-testid="journey-send-btn"]', "Send link", "Emails the guest one link for everything. Track progress in Journeys."),
  ] },
};

export function DialogTour({ open, tourKey }) {
  const cfg = DIALOG_TOURS[tourKey];
  const [steps, setSteps] = useState(null);
  useEffect(() => {
    if (!open) { setSteps(null); return undefined; }
    let timer;
    const start = (delay) => { timer = setTimeout(() => {
      const found = cfg.steps.filter((s) => document.querySelector(s.sel));
      if (found.length) setSteps(found);
    }, delay); };
    api.get("/me/tour").then((r) => { if (r.data.seen && !(r.data.pages || []).includes(tourKey)) start(500); }).catch(() => {});
    const onReplay = (e) => { if (e.detail === tourKey) start(0); };
    window.addEventListener("ns-page-tour", onReplay);
    return () => { clearTimeout(timer); window.removeEventListener("ns-page-tour", onReplay); };
  }, [open, tourKey, cfg]);
  if (!steps) return null;
  const done = () => { setSteps(null); api.post("/me/tour", { page: tourKey, seen: true }).catch(() => {}); };
  return <Spotlight steps={steps} onDone={done} testPrefix={cfg.prefix} />;
}

export function DialogTourButton({ tourKey }) {
  return (
    <button type="button" onClick={() => window.dispatchEvent(new CustomEvent("ns-page-tour", { detail: tourKey }))}
      data-testid={`${DIALOG_TOURS[tourKey].prefix}-btn`}
      className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-teal-200 text-teal-700 hover:bg-teal-50 transition-colors">
      <HelpCircle className="w-3.5 h-3.5" /> How this works
    </button>
  );
}

export const BuilderTour = ({ open }) => <DialogTour open={open} tourKey="/campaigns#builder" />;
export const BuilderTourButton = () => <DialogTourButton tourKey="/campaigns#builder" />;

export const keepDialogOpenForTour = (e) => {
  if (e.target?.closest?.("[data-tour-root]")) e.preventDefault();
};

export function PageTour() {
  const { pathname } = useLocation();
  const key = pageKey(pathname);
  const [steps, setSteps] = useState(null);
  useEffect(() => {
    if (!key) return undefined;
    let cancelled = false;
    const start = (delay) => setTimeout(() => {
      if (cancelled) return;
      const found = PAGE_TOURS[key].filter((s) => document.querySelector(s.sel));
      if (found.length) setSteps(found);
    }, delay);
    let timer;
    const auto = () => api.get("/me/tour").then((r) => {
      if (r.data.seen && !(r.data.pages || []).includes(key)) timer = start(1200);
    }).catch(() => {});
    auto();
    const onReplay = (e) => { if (e.detail === key) timer = start(0); };
    window.addEventListener("ns-page-tour", onReplay);
    window.addEventListener("ns-main-tour-done", auto);
    return () => { cancelled = true; clearTimeout(timer); window.removeEventListener("ns-page-tour", onReplay); window.removeEventListener("ns-main-tour-done", auto); };
  }, [key]);
  if (!steps) return null;
  const done = () => { setSteps(null); api.post("/me/tour", { page: key, seen: true }).catch(() => {}); };
  return <Spotlight steps={steps} onDone={done} testPrefix="page-tour" />;
}
